import { Color } from '@coast/schemas';
import { isEmpty, isNil, uniqBy } from 'lodash';
import { CSSProperties, useCallback, useId, useMemo, useState } from 'react';
import { Tag } from '@shared/components/Tag';
import { useToast } from '@shared/components/Toast';
import { View } from '@shared/components/View';
import { TagField } from '@shared/data/entity/fields/TagField';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { EditFieldContainer } from '../EditFieldContainer';
import { LookupEditFieldContainerProps } from '../EditFieldContainerProps';
import { TagEditFieldMenu } from './TagEditFieldMenu';
import { useAddTag } from './useAddTag';
import { useUpdateTag } from './useUpdateTag';

interface Props extends LookupEditFieldContainerProps {
  field: TagField | null;
  component: API.TagComponent | API.RelatedCardLookupComponent;
  workflowTemplateId: string;
  updateField?: (componentId: string, field: TagField | null) => void;
  allowCreate?: boolean;
  allowClear?: boolean;
}

export function TagEditField({
  field,
  component,
  updateField,
  workflowTemplateId,
  error,
  allowCreate,
  allowClear,
  showIcon,
  label,
  showLabel = true,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const fieldId = useId();
  const toast = useToast();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { addTag } = useAddTag(workflowTemplateId, component.id);
  const { updateTag } = useUpdateTag(workflowTemplateId, component.id);
  const [recentlyCreatedTagOptions, setRecentlyCreatedTagOptions] = useState<
    API.TagComponentOption[]
  >([]);
  const tagOptions = useMemo(
    () => (isTagComponent(component) ? (component.options ?? []) : (field?.tagOptions ?? [])),
    [component, field],
  );
  const isMultiselect = isTagComponent(component)
    ? (component.multiselect ?? false)
    : (field?.multiselect ?? false);
  const addAdditionalValues = isTagComponent(component)
    ? (component.addAdditionalValues ?? false)
    : (field?.addAdditionalValues ?? false);
  const componentOptions = useMemo(() => {
    return uniqBy([...recentlyCreatedTagOptions, ...tagOptions], t => t.value);
  }, [tagOptions, recentlyCreatedTagOptions]);
  const selectedTags = useMemo<API.TagComponentOption[]>(() => {
    const selected = field?.tagIds ?? [];
    return selected
      .map((id: string) => componentOptions.find(item => item.value === id))
      .filter(notNil);
  }, [field?.tagIds, componentOptions]);

  const onChange = useCallback(
    (selection: string[]) => {
      updateField?.(
        component.id,
        !isNil(selection) && !isEmpty(selection)
          ? new TagField(selection, component.id, tagOptions, isMultiselect, addAdditionalValues)
          : null,
      );
    },
    [component.id, tagOptions, isMultiselect, addAdditionalValues, updateField],
  );

  const createAndSelectNew = useCallback(
    async (value: string) => {
      const showCreateError = () => {
        toast({ style: 'warning', message: i18n('errors.tag.create') });
      };
      try {
        const result = await addTag(value);
        // addTag resolves with nothing when it can't find the component to add to
        if (isNil(result)) {
          showCreateError();
          return;
        }
        const newSelectedTags = isMultiselect
          ? [...selectedTags.map(tag => tag.value), result.value]
          : [result.value];
        setRecentlyCreatedTagOptions([...recentlyCreatedTagOptions, result]);
        updateField?.(
          component.id,
          new TagField(
            newSelectedTags,
            component.id,
            [...tagOptions, result],
            isMultiselect,
            addAdditionalValues,
          ),
        );
      } catch (err) {
        showCreateError();
        console.error(String(err));
      }
    },
    [
      toast,
      addTag,
      component.id,
      isMultiselect,
      tagOptions,
      recentlyCreatedTagOptions,
      selectedTags,
      updateField,
      addAdditionalValues,
    ],
  );

  const onCreate = allowCreate ? createAndSelectNew : undefined;

  const onChangeLabel = useCallback(
    async (value: string, newLabel: string) => {
      await updateTag(value, { label: newLabel });
    },
    [updateTag],
  );

  const onArchive = useCallback(
    async (value: string) => {
      await updateTag(value, { archived: true });
      setRecentlyCreatedTagOptions(prev => prev.filter(option => option.value !== value));
    },
    [updateTag],
  );

  const onUnarchive = useCallback(
    async (value: string) => {
      await updateTag(value, { archived: false });
    },
    [updateTag],
  );

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const hasTags = selectedTags.length > 0;
  const isReadOnly = !!component.readonly || isNil(updateField);
  const showClearButton = hasTags && !isReadOnly && allowClear;
  const anchorName = getAnchorName('tag-edit-field', component.id, fieldId);
  const printContent = selectedTags.map(t => t.label).join(', ') || undefined;

  // Read-only fields show the summary alone: no menu to open, and no error
  const onPress = isReadOnly ? undefined : () => setIsMenuOpen(true);
  const summaryError = isReadOnly ? undefined : error;

  return (
    <>
      <TagFieldSummary
        component={component}
        anchorName={anchorName}
        selectedTags={selectedTags}
        hasTags={hasTags}
        onPress={onPress}
        onClear={showClearButton ? onClear : undefined}
        error={summaryError}
        isReadOnly={isReadOnly}
        showIcon={showIcon}
        label={label}
        showLabel={showLabel}
        header={header}
        footer={footer}
        subfooter={subfooter}
        isGrayScale={isGrayScale}
        isItalic={isItalic}
        style={style}
        printContent={printContent}
      />
      {!isReadOnly && (
        <TagEditFieldMenu
          isOpen={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          anchorName={anchorName}
          label={component.label ?? ''}
          options={componentOptions}
          selectedValues={selectedTags.map(tag => tag.value)}
          isMultiselect={isMultiselect}
          onChange={onChange}
          onCreate={onCreate}
          isEditable={addAdditionalValues}
          onRename={onChangeLabel}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
        />
      )}
    </>
  );
}

interface TagFieldSummaryProps extends LookupEditFieldContainerProps {
  component: API.TagComponent | API.RelatedCardLookupComponent;
  anchorName: string;
  selectedTags: API.TagComponentOption[];
  onPress?: () => void;
  onClear?: () => void;
  hasTags: boolean;
  error?: string;
  isReadOnly: boolean;
  printContent?: string;
}

function TagFieldSummary({
  component,
  anchorName,
  selectedTags,
  onPress,
  onClear,
  hasTags,
  error,
  isReadOnly,
  showIcon,
  label,
  showLabel,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
  printContent,
}: TagFieldSummaryProps) {
  const showClearButton = hasTags && !isReadOnly;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showIcon={showIcon}
      label={label}
      showLabel={showLabel}
      showPlaceholder={!hasTags}
      showClearButton={showClearButton}
      // A tag the component has no option for renders as nothing — `selectedTags` maps the ids the
      // field holds through the options and drops what does not match — so a field holding only
      // those is blank, and the provenance mark under it stands down rather than claiming a fill.
      hasVisibleValue={hasTags}
      onPress={onPress}
      onClear={onClear}
      error={error}
      header={header}
      footer={footer}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      isReadOnly={isReadOnly}
      style={style}
      printContent={printContent}
    >
      {hasTags && (
        <View row wrap paddingRight={1} marginLeft={-1}>
          {selectedTags.map(tag => (
            <Tag
              key={tag.value}
              label={tag.label}
              color={tag.backgroundColor as Color}
              archived={!!tag.archived}
              style={pillStyle}
            />
          ))}
        </View>
      )}
    </EditFieldContainer>
  );
}

const pillStyle: CSSProperties = {
  marginLeft: grid(0.5),
};
